import { captureException } from './captureException'
import { trackAuthenticationError } from '../analytics/tracking'
import { getItem } from '../storeManager'

interface ProfileFetchErrorContext {
  email?: string
  userId?: number
  userToken?: string
  error: any
}

export const trackEnhancedProfileFetchError = ({
  email,
  userId,
  userToken,
  error,
}: ProfileFetchErrorContext) => {
  const emailDomain = email ? email.split('@')[1]?.toLowerCase() : 'unknown'

  let errorCategory = 'unknown'
  let isLikelyServerError = false

  if (error?.status >= 500) {
    errorCategory = 'server_error'
    isLikelyServerError = true
  } else if (error?.status === 401) {
    errorCategory = 'auth_expired'
  } else if (error?.status === 403) {
    errorCategory = 'forbidden'
  } else if (error?.status === 429) {
    errorCategory = 'rate_limited'
  } else if (error?.message?.toLowerCase().includes('network')) {
    errorCategory = 'network_error'
  } else if (error?.message?.toLowerCase().includes('timeout')) {
    errorCategory = 'timeout'
  }

  trackAuthenticationError({
    errorType: 'profile_fetch_failure',
    authMethod: 'email',
    errorMessage:
      error?.message || 'Profile fetch failed after successful login',
    couponCode: getItem('coupon') || '',
    entryPoint: getItem('EntryPoint') || '',
    statusCode: error?.status || null,
    userAgent: navigator.userAgent,
  })

  // Create a proper Error instance for better Sentry grouping
  const sentryError = new Error(`Profile fetch ${errorCategory} failure`)
  sentryError.name = 'ProfileFetchError'
  sentryError.cause = error // Preserve original error

  captureException(sentryError, {
    context: {
      tags: {
        error_category: errorCategory,
        email_domain: emailDomain,
        likely_server_error: isLikelyServerError,
        login_stage: 'profile_fetch',
      },
      extra: {
        original_error: error, // Include original error in extra data
        user_context: {
          user_id: userId,
          has_token: !!userToken,
          token_length: userToken?.length || 0,
          email_length: email?.length || 0,
        },
        browser_info: {
          language: navigator.language,
          platform: navigator.platform,
          cookieEnabled: navigator.cookieEnabled,
        },
        error_details: {
          status: error?.status,
          statusText: error?.statusText,
          message: error?.message,
          stack: error?.stack,
        },
        timing: {
          timestamp: Date.now(),
          user_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
      },
    },
    fingerprint: [
      'profile_fetch_error',
      errorCategory,
      error?.status?.toString() || 'no_status',
    ],
  })

  return {
    errorCategory,
    emailDomain,
    isLikelyServerError,
  }
}
